import { ReactNode } from 'react';
import { Box, Button, Card, Chip, Paper, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import palette from 'src/theme/palette';
import { RoleDefinition } from 'src/types/permissions.types';
import { getShellLabel } from 'src/config/permissionCatalog';

/** Accent colour per dashboard, so a role reads the same on every screen. */
export const SHELL_ACCENTS: Record<string, { color: string; icon: string }> = {
  '/admin': { color: '#2563EB', icon: 'solar:buildings-2-bold-duotone' },
  '/teacher': { color: '#D97706', icon: 'solar:case-round-minimalistic-bold-duotone' },
  '/student': { color: palette.primary.main, icon: 'solar:user-id-bold-duotone' },
};

export const roleAccent = (role: Pick<RoleDefinition, 'base_route'>) =>
  SHELL_ACCENTS[role.base_route] || SHELL_ACCENTS['/admin'];

export const personName = (m: { firstname?: string | null; lastname?: string | null; email: string }) =>
  `${m.firstname || ''} ${m.lastname || ''}`.trim() || m.email;

/** Built-in / Custom badge. */
export function RoleKindChip({ role }: { role: Pick<RoleDefinition, 'is_system'> }) {
  return (
    <Chip
      label={role.is_system ? 'Built-in' : 'Custom'}
      size="small"
      sx={{
        bgcolor: role.is_system ? '#F1F5F9' : alpha(palette.secondary.main, 0.15),
        color: role.is_system ? '#334155' : '#854D0E',
        fontWeight: 600,
        height: 20,
        fontSize: '0.6875rem',
        borderRadius: 1,
        border: role.is_system ? '1px solid #E2E8F0' : 'none',
      }}
    />
  );
}

/** Which dashboard a role lands in. */
export function ShellChip({ role }: { role: Pick<RoleDefinition, 'base_route'> }) {
  const accent = roleAccent(role);
  return (
    <Chip
      icon={<Icon icon={accent.icon} width={14} />}
      label={getShellLabel(role.base_route)}
      size="small"
      sx={{
        bgcolor: alpha(accent.color, 0.08),
        color: accent.color,
        fontWeight: 700,
        height: 22,
        fontSize: '0.7rem',
        borderRadius: 1.25,
        '& .MuiChip-icon': { color: accent.color },
      }}
    />
  );
}

interface StatCardProps {
  label: string;
  value: ReactNode;
  caption: string;
  icon: string;
  color: string;
  valueColor?: string;
}

/** KPI tile, as on the Cohorts hub. */
export function StatCard({ label, value, caption, icon, color, valueColor }: StatCardProps) {
  return (
    <Card
      sx={{
        p: 1.75,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.6875rem' }}
        >
          {label}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: valueColor || 'text.primary', my: 0.25, fontSize: '1.25rem' }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
          {caption}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: 1.75,
          bgcolor: alpha(color, 0.12),
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon icon={icon} width={20} />
      </Box>
    </Card>
  );
}

interface EmptyStateProps {
  icon: string;
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Dashed empty state, as on the cohort roster. */
export function EmptyState({ icon, title, message, actionLabel, onAction }: EmptyStateProps) {
  return (
    <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, border: '1px dashed #CBD5E0', bgcolor: '#F8FAFC', boxShadow: 'none' }}>
      <Icon icon={icon} width={34} color="#94A3B8" />
      <Typography variant="subtitle2" fontWeight={700} color="#334155" mt={1}>
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={onAction ? 1.5 : 0}>
        {message}
      </Typography>
      {onAction && actionLabel && (
        <Button
          variant="contained"
          size="small"
          onClick={onAction}
          sx={{
            bgcolor: palette.primary.main,
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: '0.75rem',
            borderRadius: 1.5,
            textTransform: 'none',
            '&:hover': { bgcolor: '#1E5631' },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Paper>
  );
}

/** Rounded filter chip, as in the Cohorts filter bar. */
export function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        height: 32,
        fontSize: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        bgcolor: active ? 'primary.main' : 'background.paper',
        color: active ? '#FFFFFF' : 'text.primary',
        border: '1px solid',
        borderColor: active ? 'primary.main' : 'divider',
        '&:hover': { bgcolor: active ? 'primary.dark' : 'grey.100' },
      }}
    />
  );
}

/** Shared look for primary / secondary header buttons. */
export const primaryButtonSx = {
  height: 36,
  px: 2,
  fontWeight: 600,
  fontSize: '0.8125rem',
  borderRadius: 1.5,
  textTransform: 'none',
  boxShadow: 'none',
  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
} as const;

export const secondaryButtonSx = {
  height: 36,
  borderRadius: 1.5,
  fontWeight: 600,
  fontSize: '0.8125rem',
  textTransform: 'none',
  borderColor: 'divider',
  color: 'text.primary',
  '&:hover': { bgcolor: 'grey.100', borderColor: 'text.secondary' },
} as const;

/** Gold confirm button used in every Cohorts dialog. */
export const goldButtonSx = {
  backgroundColor: '#EDB42E',
  color: '#000',
  fontWeight: 700,
  textTransform: 'none',
  '&:hover': { backgroundColor: '#d9a224' },
} as const;
